import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useMemo, useState } from 'react';

import { IStoriesFolder, IStoryListMessages } from '../../../../types';
import { transformFolderStructureToMap } from '../../../utils';
import { useRenameStoryFolder } from '../hooks';

import { styles } from './RenameFolderModal.styles';

export interface IRenameFolderModalProps extends RouteComponentProps {
  currentFolderId: string;
  currentFolderName: string;
  entityId: number;
  folderStructure: IStoriesFolder[];
  rootFolderId: string;
  messages: IStoryListMessages;
  onClose?(): void;
}

export const RenameFolderModal: React.FC<IRenameFolderModalProps> = props => {
  const {
    currentFolderId,
    currentFolderName,
    entityId,
    folderStructure,
    messages,
    rootFolderId
  } = props;
  const renameStoryFolderMutation = useRenameStoryFolder({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderRenameFailed
  });
  const [folderName, setFolderName] = useState(currentFolderName);
  const [hasSameName, setHasSameName] = useState(false);
  const folderMap = useMemo(() => transformFolderStructureToMap(folderStructure), [folderStructure]);
  const trimmedFolderName = useMemo(() => folderName.trim(), [folderName]);
  const parentFolderId = (folderMap[currentFolderId] && folderMap[currentFolderId].parent) || rootFolderId;
  let errorMessage;

  useEffect(
    () => {
      const parentFolder = folderMap[parentFolderId] || { children: [] };
      const siblings = parentFolder.children.filter(f => f.id !== currentFolderId);
      const hasMatch = !!siblings.find(folder => folder.displayName === trimmedFolderName);
      setHasSameName(hasMatch);
    },
    [trimmedFolderName]
  );

  const onRenameFolder = () => {
    void renameStoryFolderMutation.mutate({ id: currentFolderId, entityId, title: trimmedFolderName });
    props.onClose();
  };

  if (hasSameName) {
    errorMessage = messages.noFoldersWithSameName;
  }

  return (
    <div data-test="rename-folder">
      <Dialog open={true} onClose={props.onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="rename-folder-dialog-title">
        <DialogTitle id="rename-folder-dialog-title" sx={styles.dialogTitle}>
          {messages.renameFolder}
          <IconButton aria-label="close" onClick={props.onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">{messages.renameFolderDescription}</Typography>
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            "{currentFolderName}"
          </Typography>
          <Box sx={styles.labelRow}>
            <Typography id="rename-folder-name-label" variant="body2" sx={styles.formLabel}>{messages.name}</Typography>
            <Typography variant="caption" sx={styles.characterCounter}>
              {trimmedFolderName.length}/60
            </Typography>
          </Box>
          <TextField
            fullWidth
            required
            size="small"
            sx={styles.textField}
            placeholder={messages.folderName}
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            inputProps={{ 'data-test': 'name-input', className: 'no-format', 'aria-labelledby': 'rename-folder-name-label' }}
          />
          {errorMessage && (
            <FormHelperText error sx={styles.errorMsg}>{errorMessage}</FormHelperText>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={props.onClose}
            data-test="cancel-button"
          >
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={
              currentFolderName === trimmedFolderName ||
              trimmedFolderName.length < 3 ||
              trimmedFolderName.length > 60 ||
              hasSameName
            }
            onClick={onRenameFolder}
            data-test="rename-button"
          >
            {messages.rename}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
